/**
 *  My Home 페이지 (New)
 *
 *  @author  Bruce.Cheong   2026-04-27
 *
 */

/** External Imports */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/** Internal Imports */
import CSS from "./MyHome.module.css";
import MainTitle from "@/Component/Elements/MainTitle/MainTitle";
import CardContainer from "@/Component/Elements/CardContainer/CardContainer";
import Button from "@/Component/Elements/Button/Normal/Button";
import { getAccountData } from "@/Common/ts/commonTools";   // 접속한 사용자 정보

// 이미지
import { ReactComponent as PictureNoDataImage } from "./MyHome/img/PictureNoData.svg";
import { ReactComponent as AlertNoDataImage } from "./MyHome/img/AlertNoData.svg";

// 종속 컴포넌트
import { MainAlert } from "./MyHome/MainAlert";
import ConfirmNotification from "./MyHome/ConfirmNotification";
import ScheduleNotification from "./MyHome/ScheduleNotification";

// fetch.ts import
import fetchBE from "@/Common/fetch/fetchBE";







function MyHome(iProps: Record<string, never>) {
    const navigate = useNavigate();
    const NOTIFICATION_PREVIEW_COUNT = 3;

    /** 스케줄 fetchData (다음일정) */
    const [fetchScheduleAfter, setFetchScheduleAfter]   = useState([] as Record<string, any>[]);
    /** 스케줄 fetchData (지난일정) */
    const [fetchScheduleBefore, setFetchScheduleBefore] = useState([] as Record<string, any>[]);
    /** 알림 fetchData */
    const [fetchNotiList, setFetchNotiList]             = useState([] as Record<string, any>[]);
    /** 알림 더보기 */
    const [isNotiExpanded, setIsNotiExpanded]           = useState<boolean>(false);
    /** 촬영사진 fetchData */
    const [fetchShootPhotoList, setFetchShootPhotoList] = useState([] as Record<string, any>[]);
    /** 사진선택 fetchData */
    const [fetchPhotoSelectList, setFetchPhotoSelectList] = useState([] as Record<string, any>[]);





    useEffect(() => {
        (async () => {
            let _fetchData: Record<string, any>;





            //
            //  촬영 일정 (계약 촬영) fetch
            //

            _fetchData = await fetchBE.get.myScheduleList({
                main_type: 0,
                status_type: 0,
            });

            //
            // 지난 일정, 남은 일정 구분하여 저장
            //

            if ( _fetchData.isSuccess && _fetchData.data && _fetchData.data.length > 0 ) {
                _fetchData.data.forEach((iRow: Record<string, any>) => {
                    if( iRow.d_day_num >= 0){
                        // 남은 일정
                        setFetchScheduleAfter((iPrev) => ([ ...iPrev , iRow ]));
                    } else {
                        // 지난 일정
                        setFetchScheduleBefore((iPrev) => ([ ...iPrev , iRow ]));
                    }
                });
            }





            //
            //  촬영 일정 (단독 촬영) fetch
            //

            _fetchData = await fetchBE.get.myScheduleList({
                main_type: 1,
                status_type: 0,
            });

            if ( _fetchData.isSuccess && _fetchData.data && _fetchData.data.length > 0 ) {
                _fetchData.data.forEach((iRow: Record<string, any>) => {
                    if( iRow.d_day_num >= 0){
                        // 남은 일정
                        setFetchScheduleAfter((iPrev) => ([ ...iPrev , iRow ]));
                    } else {
                        // 지난 일정
                        setFetchScheduleBefore((iPrev) => ([ ...iPrev , iRow ]));
                    }
                });
            }





            //
            // fetchScheduleAfter, fetchScheduleBefore 의 데이타 순서를 detail.schedule_date, detail.photo_start 기준으로 오름차순 정렬
            //
            setFetchScheduleAfter((iPrev) => (
                [...iPrev].sort((a, b) => {
                    if (a.detail.schedule_date == b.detail.schedule_date) {
                        return a.detail.photo_start.localeCompare(b.detail.photo_start);
                    }
                    return a.detail.schedule_date.localeCompare(b.detail.schedule_date);
                })
            ));
            setFetchScheduleBefore((iPrev) => (
                [...iPrev].sort((a, b) => {
                    if (a.detail.schedule_date == b.detail.schedule_date) {
                        return a.detail.photo_start.localeCompare(b.detail.photo_start);
                    }
                    return a.detail.schedule_date.localeCompare(b.detail.schedule_date);
                })
            ));





            //
            //  알림 fetch
            //
            _fetchData = await fetchBE.get.myNotiList();

            if ( _fetchData.isSuccess && _fetchData.data && _fetchData.data.length > 0 ) {
                setFetchNotiList([..._fetchData.data]);
            }





            //
            //  사진 fetch
            //
            _fetchData = await fetchBE.get.myPhotoList({
                main_type: 1,
                status: 1,
            });

            if ( _fetchData.isSuccess && _fetchData.data && _fetchData.data.length > 0 ) {
                setFetchShootPhotoList([..._fetchData.data]);
            }





            //
            //  사진선택 fetch
            //
            _fetchData = await fetchBE.get.myPhotoSelectUploadList({
                search_type: 0,
            });

            if ( _fetchData.isSuccess && _fetchData.data && _fetchData.data.length > 0 ) {
                setFetchPhotoSelectList([..._fetchData.data]);
            }

        })();





        return () => {
            setFetchScheduleAfter([]);
            setFetchScheduleBefore([]);
            setFetchNotiList([]);
            setFetchShootPhotoList([]);
            setFetchPhotoSelectList([]);
        };
    }, []);

    return (
        <div className={CSS.MyHome}>
            <MainTitle title="홈" />

            <MainAlert
                user={{ name        :   getAccountData.name() }}
                data={{
                    scheduleAfter   :   fetchScheduleAfter,
                    scheduleBefore  :   fetchScheduleBefore,
                    notiList        :   fetchNotiList,
                    shootPhotoList  :   fetchShootPhotoList,
                    photoSelectList :   fetchPhotoSelectList
                }}
            />

            <div className={CSS.ContentsSection}>
                <div className={CSS.ContentsLeftSection}>
                    <ScheduleNotification
                        scheduleAfterList={fetchScheduleAfter}
                        scheduleBeforeList={fetchScheduleBefore}
                    />

                    <ConfirmNotification />
                </div>

                <div className={CSS.ContentsRightSection}>
                    <CardContainer
                        class_name_list={[CSS.ArticleCard]}
                    >
                        <h3 className={CSS.Title}>
                            최신 알림
                        </h3>
                        <div
                            className={
                                fetchNotiList && fetchNotiList.length > 0
                                    ? [CSS.Description, CSS.AlertListDescription].join(" ")
                                    : [CSS.Description, CSS.NoData].join(" ")
                            }
                        >

                            {/*
                                fetchNotiList iRow 예시

                                {
                                    "alarm_id": 26742,
                                    "alarm_title": "새로운 촬영 일정 추가",
                                    "alarm_content": "새로운 촬영 일정을 확인해 보세요!",
                                    "alarm_redirect_url": "/reservationHome?tab1=1&back_point=/&push_id=26790",
                                    "alarm_insert_dt": "20260429152746",
                                    "alarm_read_yn": 2,
                                    "push_history_id": 26790
                                }
                            */}

                            {fetchNotiList && fetchNotiList.length > 0 ? (
                                <>
                                    <ul
                                        className={[
                                            CSS.ItemListWrap,
                                            isNotiExpanded && CSS.ItemListWrapExpanded
                                        ].filter(Boolean).join(" ")}
                                    >
                                        {(isNotiExpanded
                                            ? fetchNotiList
                                            : fetchNotiList.slice(0, NOTIFICATION_PREVIEW_COUNT)
                                        ).map((iRow: Record<string, any>, index: number) => (
                                            <li
                                                className={[
                                                    CSS.ItemCard,
                                                    Number(iRow.alarm_read_yn) === 2 && CSS.ItemCardUnread,
                                                    iRow.alarm_redirect_url && CSS.ItemCardInteractive
                                                ].filter(Boolean).join(" ")}
                                                key={index}
                                                onClick={() => {
                                                    if (iRow.alarm_redirect_url) {
                                                        navigate(iRow.alarm_redirect_url);
                                                    }
                                                }}
                                                onKeyDown={(event) => {
                                                    if ((event.key === "Enter" || event.key === " ") && iRow.alarm_redirect_url) {
                                                        event.preventDefault();
                                                        navigate(iRow.alarm_redirect_url);
                                                    }
                                                }}
                                                role={iRow.alarm_redirect_url ? "button" : undefined}
                                                tabIndex={iRow.alarm_redirect_url ? 0 : undefined}
                                            >
                                                <span
                                                    className={[
                                                        CSS.TitleWrap,
                                                        Number(iRow.alarm_read_yn) === 2 && CSS.TitleWrapUnread
                                                    ].filter(Boolean).join(" ")}
                                                >
                                                    {iRow.alarm_title}
                                                </span>
                                                &nbsp;&nbsp;
                                                {iRow.alarm_content}
                                            </li>
                                        ))}
                                    </ul>

                                    {(!isNotiExpanded && fetchNotiList.length > NOTIFICATION_PREVIEW_COUNT) && (
                                        <Button
                                            onClick={() => {
                                                setIsNotiExpanded(true);
                                            }}
                                        >
                                            더보기
                                        </Button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className={CSS.ArticleCardImageWrap}>
                                        <AlertNoDataImage
                                            className={CSS.NoDataImage}
                                            aria-hidden="true"
                                            focusable="false"
                                        />
                                    </div>
                                    새로운 알림이 없습니다.
                                </>
                            )}

                        </div>
                    </CardContainer>

                    <CardContainer class_name_list={[CSS.ArticleCard]}>
                        <h3 className={CSS.Title}>
                            최근 촬영 원본
                        </h3>
                        <div>
                            {/*
                                fetchShootPhotoList iRow 예시

                                {
                                    "schedule_id": 738664,
                                    "title": "가족",
                                    "schedule_date": "2026-05-05",
                                    "schedule_date_day": "Tuesday",
                                    "office_id": 4,
                                    "office_name": "럽맘",
                                    "upload_date": "2026-05-14",
                                    "thumbnail": {
                                        "org_url": "https://b2001cf1b9a5aaa314f5e84444a4fe9e.r2.cloudflarestorage.com/lmedrp/dev/1/office/4/customers/202605/1224566/schedules/738664/before/1%20%281%29.JPG?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=168b01c8ba4c49a6311b176b5b14774d%2F20260514%2Fauto%2Fs3%2Faws4_request&X-Amz-Date=20260514T054504Z&X-Amz-Expires=600&X-Amz-SignedHeaders=host&X-Amz-Signature=fb4e0aab78979d7cc959c55d1b921c50ac64f8481bdfed6c4d3a85f4dc925935",
                                        "small_url": "https://b2001cf1b9a5aaa314f5e84444a4fe9e.r2.cloudflarestorage.com/lmedrp-small/dev/1/office/4/customers/202605/1224566/schedules/738664/before/1%20%281%29.JPG?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=168b01c8ba4c49a6311b176b5b14774d%2F20260514%2Fauto%2Fs3%2Faws4_request&X-Amz-Date=20260514T054504Z&X-Amz-Expires=600&X-Amz-SignedHeaders=host&X-Amz-Signature=618117abde96b4027113d409a3705529bba2830de7673e5a57e55cc42c53e278",
                                        "middle_url": "https://b2001cf1b9a5aaa314f5e84444a4fe9e.r2.cloudflarestorage.com/lmedrp-middle/dev/1/office/4/customers/202605/1224566/schedules/738664/before/1%20%281%29.JPG?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=168b01c8ba4c49a6311b176b5b14774d%2F20260514%2Fauto%2Fs3%2Faws4_request&X-Amz-Date=20260514T054504Z&X-Amz-Expires=600&X-Amz-SignedHeaders=host&X-Amz-Signature=819862ceddb3eb5b119f382f50810cf09b7c8dc84e419c5feec9ad3bd128ca4e"
                                    },
                                    "main_type": "1",
                                    "photo_type": "본촬영",
                                    "new_download_file_count": 1,
                                    "joined_event": "N"
                                }
                            */}

                            {fetchShootPhotoList && fetchShootPhotoList.length > 0 ? (
                                <div className={CSS.PhotoPreviewSection}>
                                    <ul className={CSS.PhotoPreviewGrid}>
                                        {fetchShootPhotoList.slice(0, 2).map((iRow: Record<string, any>, index: number) => {
                                            const thumbnailUrl =
                                                iRow.thumbnail?.small_url
                                                || iRow.thumbnail?.middle_url
                                                || iRow.thumbnail?.org_url
                                                || "";

                                            return (
                                                <li
                                                    className={CSS.PhotoPreviewItem}
                                                    key={index}
                                                    title={`${iRow.title} ${iRow.schedule_date}`}
                                                    onClick={() => {
                                                        navigate("/photoList");
                                                    }}
                                                >
                                                    <div className={CSS.PhotoPreviewFrame}>
                                                        {thumbnailUrl ? (
                                                            <img
                                                                src={thumbnailUrl}
                                                                alt={`${iRow.title} 썸네일`}
                                                                className={CSS.PhotoPreviewImage}
                                                            />
                                                        ) : (
                                                            <div className={CSS.PhotoPreviewEmpty}>
                                                                <PictureNoDataImage
                                                                    className={CSS.PhotoPreviewEmptyIcon}
                                                                    aria-hidden="true"
                                                                    focusable="false"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>

                                    {fetchShootPhotoList.length > 2 && (
                                        <Button
                                            class_name_list={[CSS.PhotoMoreButton]}
                                            onClick={() => {
                                                navigate("/photoList");
                                            }}
                                        >
                                            더보기
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className={CSS.ArticleCardImageWrap}>
                                        <PictureNoDataImage
                                            className={CSS.NoDataImage}
                                            aria-hidden="true"
                                            focusable="false"
                                        />
                                    </div>
                                    업로드된 사진이 없습니다.
                                </>
                            )}

                        </div>
                    </CardContainer>

                    {/* 
                        2026.05.18 김효민 
                        기능개발 완료 후 활성화 예정
                    */}
                    {/* <CardContainer class_name_list={[CSS.ArticleCard]}>
                        <h3 className={CSS.Title}>
                            도움이 필요하신가요?
                        </h3>
                        <div className={[CSS.Description].join(" ")}>
                            서비스 이용 중 궁금한 점을 남겨주시면 스튜디오 매니저가 빠르게 답변해 드립니다.<br />
                            <div className={CSS.HelpActionGroup}>
                                <Button
                                    class_name_list={[CSS.HelpActionButton]}
                                    onClick={() => {
                                        navigate("/faqList");
                                    }}
                                >
                                    FAQ
                                </Button>
                                <Button
                                    class_name_list={[CSS.HelpActionButton]}
                                    onClick={() => {
                                        navigate("/qnaList");
                                    }}
                                >
                                    1:1문의
                                </Button>
                            </div>
                        </div>
                    </CardContainer> */}
                </div>
            </div>
        </div>
    );

}




export default MyHome;
